import { Typography, Container } from '@cleartrip/ct-design-components'
import { useState } from 'react'
import { filterTypes } from '@/constants/Srp'
import CheckboxItem from './checkboxItem'
import FilterJourneyInfo from './FilterJourneyInfo'

interface LayoverAirportData {
  [country: string]: string[]
}
interface LayoverAirportProps {
  onFilterChange: (filterType: any, selectedValues: string[]) => void
  layoverAirport: {
    availableLayoverAirport: LayoverAirportData
    selectedOptions?: any
  }
  from?: string
  to?: string
  isRT?: boolean
  isRevamp?: boolean
}

const LayoverAirportFilter = ({
  onFilterChange,
  layoverAirport,
  from,
  to,
  isRT,
  isRevamp = false,
}: LayoverAirportProps) => {
  const [showAllCountries, setShowAllCountries] = useState<{ [key: string]: boolean }>({})
  const { availableLayoverAirport = {}, selectedOptions = {} } = layoverAirport || {}

  const selectedValues: string[] = []
  if (selectedOptions && typeof selectedOptions === 'object') {
    Object.entries(selectedOptions).forEach(([country, airports]: [string, any]) => {
      if (Array.isArray(airports)) {
        airports.forEach((airport: string) => {
          selectedValues.push(airport)
        })
      }
    })
  }
  const onLayoverAirportClick = (value: string) => {
    const updatedSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter((selectedValue: string) => selectedValue !== value)
      : [...selectedValues, value]
    onFilterChange(filterTypes.LAYOVER_AIRPORT, updatedSelectedValues)
  }

  const toggleShowAllForCountry = (country: string) => {
    setShowAllCountries(prev => ({
      ...prev,
      [country]: !prev[country],
    }))
  }

  const getDisplayedOptionsForCountry = (country: string, airports: string[]) => {
    const showAll = showAllCountries[country]
    return showAll ? airports : airports.slice(0, 5) // Show first 5 by default
  }

  const checkboxFor = (airport: string) => (
    <CheckboxItem
      key={airport}
      checked={selectedValues.includes(airport)}
      label={
        <Typography className='c-pointer' variant='P2'>
          {airport}
        </Typography>
      }
      wrapperClassName='py-2 checkbox-hover-border'
      onClick={() => onLayoverAirportClick(airport)}
    />
  )

  if (isRevamp) {
    /** One flat list — the revamp drops the per-country grouping. */
    const allAirports = Object.values(availableLayoverAirport).flat()
    const showAll = showAllCountries.all
    const remainingCount = allAirports.length - 5
    const visibleAirports = showAll ? allAirports : allAirports.slice(0, 5)
    const airportsWord = remainingCount === 1 ? 'airport' : 'airports'
    const showMoreText = showAll ? 'Show less' : `+ ${remainingCount} more layover ${airportsWord}`

    return (
      <Container className='flex flex-column'>
        <FilterJourneyInfo from={from || ''} to={to || ''} isRT={!!isRT} isRevamp />
        {visibleAirports.map(checkboxFor)}
        {remainingCount > 0 && (
          <Container className='py-2'>
            <Typography
              className='c-pointer c-primary'
              color='link'
              variant='L2'
              onClick={() => toggleShowAllForCountry('all')}
            >
              {showMoreText}
            </Typography>
          </Container>
        )}
      </Container>
    )
  }

  return (
    <>
      <FilterJourneyInfo from={from || ''} to={to || ''} isRT={!!isRT} legacyClassName='c-pointer py-3' />
      {Object.entries(availableLayoverAirport).map(([country, airports]) => {
        const displayedAirports = getDisplayedOptionsForCountry(country, airports)
        const remainingCount = airports.length - 5

        return (
          <Container key={country}>
            <Typography variant='B3CAPS' color='subheading' className='py-2'>
              {country}
            </Typography>

            {displayedAirports.map((airport: string) => {
              const checked = selectedValues.includes(airport)

              return (
                <CheckboxItem
                  key={airport}
                  label={
                    <Typography variant='B2' className='c-pointer'>
                      {airport}
                    </Typography>
                  }
                  checked={checked}
                  onClick={() => onLayoverAirportClick(airport)}
                  wrapperClassName='py-2 checkbox-hover-border'
                />
              )
            })}

            {!showAllCountries[country] && remainingCount > 0 && (
              <div className='py-2'>
                <Typography
                  variant='L2'
                  className='c-pointer c-primary'
                  isClickable={true}
                  onClick={() => toggleShowAllForCountry(country)}
                  color='link'
                >
                  + {remainingCount} more layover airport{remainingCount !== 1 ? 's' : ''}
                </Typography>
              </div>
            )}

            {showAllCountries[country] && airports.length > 5 && (
              <div className='mt-2'>
                <Typography
                  variant='L2'
                  className='c-pointer c-primary'
                  isClickable={true}
                  color='link'
                  onClick={() => toggleShowAllForCountry(country)}
                >
                  Show less
                </Typography>
              </div>
            )}
          </Container>
        )
      })}
    </>
  )
}

export default LayoverAirportFilter
